import React, { useContext, useMemo, useEffect, useState } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import { GroupBase } from "react-select";
import {
  Loader,
  ReactSelectController,
  ReactSelectControllerProps,
  ReactSelectSyncProps,
} from "@pushpress/shared-components";
import { AppContext } from "../../../context/app";
import { sortByName } from "../../../utils/sortUtils";
import { Plan, getPlans } from "../../../apis/subscriptionApi";

type PlanOption = Plan;

type PlanControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends PlanOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = ReactSelectControllerProps<TFieldValues, TName, Option, IsMulti, Group>;

function PlanSelectController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends PlanOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: PlanControllerProps<TFieldValues, TName, Option, IsMulti, Group> &
    ReactSelectSyncProps,
) {
  const app = useContext(AppContext);
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const plans = await getPlans();
        setPlans(plans || []);
      } catch (error) {
        console.error("Error fetching plans:", error);
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };

    if (app.client.uuid) {
      fetchPlans();
    }
  }, [app.client.uuid]);

  const getOptionLabel = (option: PlanOption) =>
    option.name;

  const getOptionValue = (option: PlanOption) =>
    option.planUuid;

  const options = useMemo(() => {
    return [...plans].sort(sortByName);
  }, [plans]);

  if (loading) {
    return <Loader />;
  }

  return (
    <ReactSelectController
      {...props}
      placeholder="— Select —"
      menuPortalTarget={document.body}
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
    />
  );
}

export default PlanSelectController;
